import React, { useEffect } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import EpdRoutes from 'routes/EpdRoutes';
import AuthService from 'services/AuthService';
import { ResetPasswordModel } from 'services/EpdClient';
import { PoorMansError } from 'util/utils';
import * as Yup from 'yup';
import { passwordValidationSchema } from 'constants/userValidationSchemes';
import AuthLayoutFrame from '../../../components/auth/AuthLayoutFrame';
import styled from 'styled-components';
import AuthTitle from '../../../components/auth/AuthTitle';
import { PrimaryButton } from 'components/v2/buttons';
import InputFormField from 'components/v2/form/InputFormField';
import { toaster } from 'components/v2/toast';
import AuthStatusPage from 'components/auth/AuthStatusPage';

const ResetPassword: React.FC<RouteComponentProps> = ({ location, history }) => {
  const [isTokenValidationComplete, setIsTokenValidationComplete] = React.useState<boolean>(false);
  const values = new URLSearchParams(location.search);
  const userName = values.get('userName');
  const token = values.get('token');
  const { t } = useTranslation();

  useEffect(() => {
    if (!token || !userName) {
      // Handled in this component
      return;
    }

    const abortController = new AbortController();

    const checkToken = async () => {
      try {
        await AuthService.validateResetPasswordToken({ userName, token }, { signal: abortController.signal });
        setIsTokenValidationComplete(true);
      } catch (e) {
        if (abortController.signal.aborted) {
          return;
        }
        console.error('Error validating reset password token', e);
        history.push(EpdRoutes.account.resetPasswordTokenError);
      }
    };
    checkToken();

    return () => abortController.abort();
  }, []);

  const initialValues: ResetPasswordModel = {
    userName: userName || '',
    password: '',
    confirmPassword: '',
    token: token || '',
  };

  const resetPasswordSchema = Yup.object({
    password: passwordValidationSchema(t),
    confirmPassword: Yup.string()
      .nullable()
      .oneOf([Yup.ref('password')], t('account.validation.passwordsNotMatch'))
      .required(t('account.validation.confirmPasswordRequired')),
  });

  const resetPasword = async (values: ResetPasswordModel) => {
    try {
      await AuthService.resetPassword(values);
      history.push(EpdRoutes.account.resetPasswordSuccess);
    } catch (e) {
      const error = PoorMansError(e);
      toaster({ severity: 'error', summary: 'Unexpected error', details: error });
    }
  };

  if (!token || !userName) {
    return (
      <AuthLayoutFrame>
        <BodyContainer>
          <Heading>
            <AuthTitle>Reset your password</AuthTitle>
            <Instructions>Oops. It appears the link is you used to reset your password is faulty</Instructions>
          </Heading>
        </BodyContainer>
      </AuthLayoutFrame>
    );
  }

  if (!isTokenValidationComplete) {
    return <AuthStatusPage imageSkeleton title="Loading..." />;
  }

  return (
    <AuthLayoutFrame>
      <BodyContainer>
        <Heading>
          <AuthTitle>Reset your password</AuthTitle>
          <Instructions>Please enter a new password and confirm it below.</Instructions>
        </Heading>
        <Formik initialValues={initialValues} validationSchema={resetPasswordSchema} onSubmit={resetPasword}>
          {({ handleSubmit, isSubmitting, isValid, dirty }) => (
            <FormWrapper onSubmit={handleSubmit}>
              <div>
                <InputFormField type="password" label={t('account.password')} name="password" errorPositioning="static" />
                <PasswordRequirements>
                  Password must be at least 6 characters and include upper and lower case letters, a number, and a special
                  symbol.
                </PasswordRequirements>
              </div>

              <InputFormField
                type="password"
                label={t('account.confirmPassword')}
                name="confirmPassword"
                errorPositioning="absolute"
              />
              <SubmitButton type="submit" disabled={isSubmitting || !isValid || !dirty}>
                {isSubmitting ? 'Resetting password...' : 'Reset password'}
              </SubmitButton>
            </FormWrapper>
          )}
        </Formik>
      </BodyContainer>
    </AuthLayoutFrame>
  );
};

export default ResetPassword;

const BodyContainer = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 40px;
  padding-bottom: 60px;
  align-self: center;

  ${(props) => props.theme.media.tablet} {
    padding-left: 80px;
    padding-right: 80px;
  }

  ${(props) => props.theme.media.desktop} {
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const Heading = styled.div`
  text-align: center;
`;

const Instructions = styled.div`
  margin-top: 30px;
  font-size: 16px;
  line-height: 24px;
`;

const FormWrapper = styled.form`
  display: grid;
  gap: 40px;
  margin-top: 60px;
`;

const PasswordRequirements = styled.div`
  font-size: 12px;
  margin-top: 4px;
`;

const SubmitButton = styled(PrimaryButton)`
  justify-content: center;
`;
